import { Component, Inject, OnInit, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { HttpService } from 'src/app/services/http.service';
import { environment } from 'src/environments/environment';


export interface DocumentPreviewData {
    documentUrl?: string | null;
    documentName?: string;
    fileType?: string;
    documentId?: string;
}

type PreviewKind = 'pdf' | 'image' | 'audio' | 'video' | 'csv' | 'text' | 'office' | 'unsupported';

const IMAGE_EXTENSIONS = ['jpg', 'jpeg', 'png', 'webp', 'gif', 'svg'];
const AUDIO_EXTENSIONS = ['mp3', 'wav', 'ogg', 'oga', 'm4a', 'aac', 'flac', 'wma'];
const VIDEO_EXTENSIONS = ['mp4', 'webm', 'mov', 'm4v', 'avi', 'mkv', 'ogv'];
const CSV_EXTENSIONS = ['csv'];
const TEXT_EXTENSIONS = ['txt', 'log', 'md', 'json', 'tsv'];
const OFFICE_EXTENSIONS = ['doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx', 'rtf'];

const MIME_TO_EXTENSION: { [mime: string]: string } = {
    'application/pdf': 'pdf',
    'image/jpeg': 'jpg',
    'image/png': 'png',
    'image/webp': 'webp',
    'image/gif': 'gif',
    'image/svg+xml': 'svg',
    'audio/mpeg': 'mp3',
    'audio/mp3': 'mp3',
    'audio/wav': 'wav',
    'audio/x-wav': 'wav',
    'audio/ogg': 'ogg',
    'audio/mp4': 'm4a',
    'audio/aac': 'aac',
    'audio/flac': 'flac',
    'video/mp4': 'mp4',
    'video/webm': 'webm',
    'video/quicktime': 'mov',
    'video/x-msvideo': 'avi',
    'video/x-matroska': 'mkv',
    'video/ogg': 'ogv',
    'text/csv': 'csv',
    'text/plain': 'txt',
    'text/markdown': 'md',
    'application/json': 'json',
    'text/tab-separated-values': 'tsv',
    'application/msword': 'doc',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'docx',
    'application/vnd.ms-excel': 'xls',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'xlsx',
    'application/vnd.ms-powerpoint': 'ppt',
    'application/vnd.openxmlformats-officedocument.presentationml.presentation': 'pptx',
    'application/rtf': 'rtf',
    'text/rtf': 'rtf',
};

@Component({
    selector: 'app-document-preview',
    imports: [CommonModule],
    templateUrl: './document-preview.component.html',
    styleUrl: './document-preview.component.scss',
})
export class DocumentPreviewComponent implements OnInit, OnDestroy {

    documentName: string;
    documentUrl: string | null;
    fileExtension: string;
    kind: PreviewKind = 'unsupported';

    safeResourceUrl: SafeResourceUrl | null = null;

    loading = true;
    loadingMessage = 'Loading document…';
    loadError = false;
    errorMessage = '';

    // Image zoom controls
    zoomLevel = 1;
    readonly minZoom = 0.5;
    readonly maxZoom = 3;

    downloadInProgress = false;

    private convertedObjectUrl: string | null = null;

    constructor(
        public dialogRef: MatDialogRef<DocumentPreviewComponent>,
        @Inject(MAT_DIALOG_DATA) public data: DocumentPreviewData,
        private sanitizer: DomSanitizer,
        private cdr: ChangeDetectorRef,
        private httpService: HttpService
    ) {
        this.documentName = data?.documentName || 'Document';
        this.documentUrl = (data?.documentUrl || '').trim() || null;
        this.fileExtension = this.resolveExtension(data?.fileType, this.documentName, this.documentUrl);
    }

    ngOnInit(): void {
        // Never attempt to render undefined/null/empty as a URL.
        if (!this.documentUrl || !this.isHttpUrl(this.documentUrl)) {
            this.loading = false;
            this.loadError = false;
            this.kind = 'unsupported';
            this.errorMessage = 'No preview URL was returned for this document.';
            return;
        }

        this.kind = this.classify(this.fileExtension);

        switch (this.kind) {
            case 'pdf':
            case 'image':
            case 'text':
                this.safeResourceUrl = this.sanitizer.bypassSecurityTrustResourceUrl(this.documentUrl);
                break;
            case 'audio':
            case 'video':
                break;
            case 'office':
            case 'csv':
                this.loadOfficePreview(this.documentUrl);
                break;
            default:
                this.loading = false;
                break;
        }
    }

    private isHttpUrl(url: string): boolean {
        return /^https?:\/\//i.test(url);
    }

    private resolveExtension(fileType?: string, name?: string, url?: string | null): string {
        if (url) {
            const fromMime = this.extensionFromUrlMimeParam(url);
            if (fromMime) {
                return fromMime;
            }
            const fromPath = this.extensionOf(this.pathnameOf(url));
            if (fromPath) {
                return fromPath;
            }
        }
        const fromType = (fileType || '').trim().toLowerCase();
        if (fromType) {
            return fromType;
        }
        const fromName = this.extensionOf(name);
        if (fromName) {
            return fromName;
        }
        return '';
    }

    private extensionFromUrlMimeParam(url: string): string {
        try {
            const parsed = new URL(url);
            const mime = (
                parsed.searchParams.get('response-content-type')
                || parsed.searchParams.get('content-type')
                || ''
            ).trim().toLowerCase().split(';')[0];
            return MIME_TO_EXTENSION[mime] || '';
        } catch {
            return '';
        }
    }

    private pathnameOf(url: string): string {
        try {
            return new URL(url).pathname;
        } catch {
         
            return url.split('?')[0];
        }
    }

    private extensionOf(value?: string): string {
        if (!value || value.indexOf('.') === -1) {
            return '';
        }
        const parts = value.split('.');
        const ext = parts[parts.length - 1].toLowerCase();
        // filename/pathname.
        if (!ext || ext.length > 10 || /[^a-z0-9]/i.test(ext)) {
            return '';
        }
        return ext;
    }

    private classify(ext: string): PreviewKind {
        if (ext === 'pdf') { return 'pdf'; }
        if (IMAGE_EXTENSIONS.includes(ext)) { return 'image'; }
        if (AUDIO_EXTENSIONS.includes(ext)) { return 'audio'; }
        if (VIDEO_EXTENSIONS.includes(ext)) { return 'video'; }
        if (CSV_EXTENSIONS.includes(ext)) { return 'csv'; }
        if (TEXT_EXTENSIONS.includes(ext)) { return 'text'; }
        if (OFFICE_EXTENSIONS.includes(ext)) { return 'office'; }
        return 'unsupported';
    }

    private loadOfficePreview(url: string): void {
        this.loading = true;
        this.loadingMessage = 'Converting document for preview…';

        this.httpService.sendPostDocRequest(environment.doc2pdf, { url }).subscribe({
            next: (buffer: ArrayBuffer) => {
                try {
                    const blob = new Blob([buffer], { type: 'application/pdf' });
                    this.convertedObjectUrl = URL.createObjectURL(blob);
                    this.safeResourceUrl = this.sanitizer.bypassSecurityTrustResourceUrl(this.convertedObjectUrl);
                    this.loading = false;
                    this.cdr.detectChanges();
                } catch {
                    this.loading = false;
                    this.loadError = true;
                    this.errorMessage = 'This file could not be converted for preview.';
                    this.cdr.detectChanges();
                }
            },
            error: () => {
                this.loading = false;
                this.loadError = true;
                this.errorMessage = 'This file could not be converted for preview.';
                this.cdr.detectChanges();
            }
        });
    }

    get showFrame(): boolean {
        return !this.loadError && !!this.safeResourceUrl
            && (this.kind === 'pdf' || this.kind === 'text' || this.kind === 'office' || this.kind === 'csv');
    }

    get showFallbackCard(): boolean {
        return !this.loading && !this.loadError && this.kind === 'unsupported';
    }

    onFrameLoad(): void {
        this.loading = false;
    }

    onFrameError(): void {
        this.loading = false;
        this.loadError = true;
        this.errorMessage = this.frameErrorMessage();
    }

    private frameErrorMessage(): string {
        switch (this.kind) {
            case 'pdf': return 'This PDF could not be displayed.';
            case 'image': return 'This image could not be displayed.';
            case 'audio': return 'This audio file could not be played.';
            case 'video': return 'This video could not be played.';
            case 'office': return 'This document could not be displayed.';
            case 'csv': return 'This spreadsheet could not be displayed.';
            default: return 'This file could not be displayed.';
        }
    }

    zoomIn(): void {
        this.zoomLevel = Math.min(this.maxZoom, +(this.zoomLevel + 0.25).toFixed(2));
    }

    zoomOut(): void {
        this.zoomLevel = Math.max(this.minZoom, +(this.zoomLevel - 0.25).toFixed(2));
    }

    resetZoom(): void {
        this.zoomLevel = 1;
    }

    get zoomPercent(): number {
        return Math.round(this.zoomLevel * 100);
    }

    download(): void {
        if (this.downloadInProgress || !this.documentUrl) {
            return;
        }
        this.downloadInProgress = true;
        const url = this.documentUrl;

        fetch(url)
            .then(res => {
                if (!res.ok) {
                    throw new Error(`Request failed with status ${res.status}`);
                }
                return res.blob();
            })
            .then(blob => {
                const blobUrl = URL.createObjectURL(blob);
                this.clickDownloadLink(blobUrl, this.documentName);
                URL.revokeObjectURL(blobUrl);
                this.downloadInProgress = false;
                this.cdr.detectChanges();
            })
            .catch(() => {
                this.clickDownloadLink(url, this.documentName, true);
                this.downloadInProgress = false;
                this.cdr.detectChanges();
            });
    }

    private clickDownloadLink(href: string, fileName: string, crossOriginFallback: boolean = false): void {
        const link = document.createElement('a');
        link.href = href;
        link.download = fileName;
        if (crossOriginFallback) {
            link.target = '_blank';
            link.rel = 'noopener';
        }
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    }

    get headerIconClass(): string {
        switch (this.kind) {
            case 'pdf': return 'fa-file-pdf-o';
            case 'image': return 'fa-file-image-o';
            case 'audio': return 'fa-file-audio-o';
            case 'video': return 'fa-file-video-o';
            case 'csv': return 'fa-table';
            case 'text': return 'fa-file-text-o';
            case 'office': return this.officeIconClass();
            default: return 'fa-file-o';
        }
    }

    private officeIconClass(): string {
        if (['doc', 'docx'].includes(this.fileExtension)) { return 'fa-file-word-o'; }
        if (['xls', 'xlsx'].includes(this.fileExtension)) { return 'fa-file-excel-o'; }
        if (['ppt', 'pptx'].includes(this.fileExtension)) { return 'fa-file-powerpoint-o'; }
        return 'fa-file-o';
    }

    close(): void {
        this.dialogRef.close();
    }

    ngOnDestroy(): void {
        // Only the office-conversion blob needs explicit cleanup — download()
        // already revokes its own blob URL immediately after use.
        if (this.convertedObjectUrl) {
            URL.revokeObjectURL(this.convertedObjectUrl);
            this.convertedObjectUrl = null;
        }
    }
}